<template>
    <section class="customer-step">
        <div class="section-heading mb-3">
            <div>
                <div class="overline primary--text">1 · Cliente</div>
                <h2 class="text-h6">Associa il contatto al cliente Sage</h2>
            </div>
            <v-chip v-if="draft.customerConfirmed" small color="success" text-color="white">
                <v-icon small left>mdi-check</v-icon>Confermato
            </v-chip>
        </div>

        <v-alert v-if="draft.customerConfirmed && !editing" type="success" text dense class="mb-3">
            <div class="d-flex flex-column flex-sm-row align-sm-center">
                <div>
                    <strong>{{ draft.customerCode }} · {{ draft.customerName }}</strong>
                    <div class="caption">
                        {{ draft.contactEmail }} · Responsabile Qualità · associazione riutilizzata per le prossime mail
                    </div>
                </div>
                <v-spacer />
                <v-btn text small color="success" class="mt-2 mt-sm-0" @click="editing = true">Cambia</v-btn>
            </div>
        </v-alert>

        <template v-else>
            <v-alert v-if="sageCandidates.length" type="info" text dense>
                {{ sageCandidates.length === 1
                    ? "Il contatto è presente in Sage: verifica e conferma il cliente proposto."
                    : "Il contatto è collegato a più clienti Sage: scegli quello corretto." }}
            </v-alert>
            <v-alert v-else type="warning" text dense>
                Contatto non riconosciuto. Cerca il cliente per codice o ragione sociale.
            </v-alert>
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-select
                        v-model="contactEmail"
                        :items="contactEmails"
                        outlined
                        dense
                        label="Contatto letto nella mail"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" md="8">
                    <v-autocomplete
                        v-model="selectedCustomer"
                        :items="combinedCustomers"
                        :loading="customerSearchLoading"
                        :search-input.sync="search"
                        :item-text="customerLabel"
                        return-object
                        outlined
                        dense
                        clearable
                        no-filter
                        label="Cliente Sage"
                        hint="Digita almeno 2 caratteri. Solo clienti con DDT di vendita negli ultimi 6 mesi, inclusi i DDT non validati"
                        persistent-hint
                        :hide-no-data="customerSearchLength < 2"
                        no-data-text="Nessun cliente con DDT di vendita recente"
                    >
                        <template v-slot:item="{ item }">
                            <v-list-item-content>
                                <v-list-item-title>{{ item.code }} · {{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ item.contact_name || item.contact_email || "Cliente Sage" }}
                                    <span v-if="item.has_recent_deliveries === false"> · nessuna consegna recente</span>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <div class="d-flex justify-end mt-3">
                <v-btn
                    color="primary"
                    :loading="associating || reanalyzing"
                    :disabled="!selectedCustomer || !contactEmail"
                    @click="openContactConfirmation"
                >
                    <v-icon left>mdi-account-plus-outline</v-icon>
                    Salva contatto in Sage
                </v-btn>
            </div>
        </template>

        <v-dialog v-model="confirmationDialog" max-width="620">
            <v-card>
                <v-card-title class="pb-2">Conferma il contatto Sage</v-card-title>
                <v-card-text>
                    <v-alert type="info" text dense class="mb-4">
                        Se il contatto esiste già per questa azienda verrà riutilizzato, senza creare duplicati.
                    </v-alert>
                    <v-text-field
                        :value="customerLabel(selectedCustomer)"
                        label="Azienda"
                        outlined
                        dense
                        readonly
                    />
                    <v-text-field
                        v-model="contactName"
                        label="Nome contatto"
                        maxlength="80"
                        outlined
                        dense
                    />
                    <v-text-field
                        :value="contactEmail"
                        label="Email"
                        outlined
                        dense
                        readonly
                    />
                    <v-text-field
                        value="Responsabile Qualità"
                        label="Funzione"
                        outlined
                        dense
                        readonly
                    >
                        <template v-slot:prepend-inner>
                            <v-icon small color="primary">mdi-shield-account-outline</v-icon>
                        </template>
                    </v-text-field>
                    <v-alert
                        v-if="selectedCustomer && selectedCustomer.has_recent_deliveries === false"
                        type="warning"
                        text
                        dense
                        class="mb-0"
                    >
                        Il cliente non ha consegne negli ultimi 6 mesi: il contatto sarà salvato, ma la ricerca
                        articoli potrebbe non proporre risultati.
                    </v-alert>
                </v-card-text>
                <v-card-actions class="px-6 pb-5">
                    <v-spacer />
                    <v-btn text :disabled="associating" @click="confirmationDialog = false">Annulla</v-btn>
                    <v-btn color="primary" :loading="associating || reanalyzing" @click="confirmCustomer">
                        <v-icon left>mdi-content-save-check-outline</v-icon>
                        Salva e cerca articoli
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-row v-if="draft.customerConfirmed" dense class="mt-1">
            <v-col cols="12" md="7">
                <v-select
                    :value="draft.addressCode"
                    :items="addresses"
                    :loading="addressesLoading"
                    item-value="codice"
                    :item-text="addressLabel"
                    outlined
                    dense
                    label="Indirizzo cliente della NC"
                    no-data-text="Nessun indirizzo disponibile"
                    @input="$emit('update-address', $event)"
                />
            </v-col>
            <v-col cols="12" md="5">
                <v-text-field
                    :value="draft.customerReference"
                    outlined
                    dense
                    maxlength="20"
                    counter="20"
                    label="Riferimento cliente (facoltativo)"
                    hint="Se vuoto verrà usato un riferimento univoco della mail"
                    persistent-hint
                    @input="$emit('update-reference', $event)"
                />
            </v-col>
        </v-row>
    </section>
</template>

<script>
export default {
    name: "MailSegnalazioneCustomer",
    props: {
        message: { type: Object, required: true },
        draft: { type: Object, required: true },
        customerOptions: { type: Array, default: () => [] },
        customerSearchLoading: { type: Boolean, default: false },
        associating: { type: Boolean, default: false },
        reanalyzing: { type: Boolean, default: false },
        addresses: { type: Array, default: () => [] },
        addressesLoading: { type: Boolean, default: false },
    },
    data: () => ({
        editing: false,
        search: "",
        selectedCustomer: null,
        contactEmail: "",
        contactName: "",
        confirmationDialog: false,
    }),
    computed: {
        contactEmails() {
            return this.message.customer_context?.emails?.length
                ? this.message.customer_context.emails
                : [this.message.sender?.email].filter(Boolean);
        },
        sageCandidates() {
            return this.message.customer_context?.candidates || [];
        },
        combinedCustomers() {
            const keyed = new Map();
            [...this.customerOptions, ...this.sageCandidates].forEach((item) => keyed.set(item.code, item));
            return [...keyed.values()];
        },
        customerSearchLength() {
            return String(this.search || "").trim().length;
        },
    },
    watch: {
        message: { immediate: true, handler() { this.initialize(); } },
        search(value) { this.$emit("search-customers", value || ""); },
        selectedCustomer(value) {
            if (value?.contact_name) this.contactName = value.contact_name;
        },
    },
    methods: {
        initialize() {
            this.editing = !this.draft.customerConfirmed;
            this.contactEmail = this.draft.contactEmail || this.contactEmails[0] || "";
            this.selectedCustomer = this.combinedCustomers.find((item) => item.code === this.draft.customerCode)
                || this.sageCandidates[0]
                || null;
            this.contactName = this.selectedCustomer?.contact_name || this.message.sender?.name || "";
            this.confirmationDialog = false;
        },
        customerLabel(item) {
            return item ? `${item.code} · ${item.name}` : "";
        },
        addressLabel(item) {
            return item ? `${item.codice} · ${item.descrizione}` : "";
        },
        openContactConfirmation() {
            this.contactName = this.selectedCustomer?.contact_name || this.contactName || this.message.sender?.name || "";
            this.confirmationDialog = true;
        },
        confirmCustomer() {
            this.$emit("confirm-customer", {
                customer: this.selectedCustomer,
                contactEmail: this.contactEmail,
                contactName: this.contactName,
            });
            this.confirmationDialog = false;
        },
    },
};
</script>

<style scoped>
.customer-step { padding: 20px 0; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
</style>
